import React, { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { HiPlus } from "react-icons/hi";
import { FaEdit, FaTrash, FaEye, FaTrophy, FaChartBar, FaBug } from "react-icons/fa";
import { IconButton } from "@mui/material";
import CreateEditEventModal from "./CreateEditEventModal";
import EventLeaderboardAdmin from "./EventLeaderboardAdmin";
import EventDashboard from "./EventDashboard";
import DiagnoseDoubleCountsModal from "./DiagnoseDoubleCountsModal";
import Swal from "sweetalert2";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import TableLoader from "Components/Common/Loader/TableLoader";

const statusColors = {
  draft: "bg-gray-200 text-gray-700",
  active: "bg-green-100 text-green-700",
  completed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-700",
};

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showDiagnose, setShowDiagnose] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchEvents = (page = 1) => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/event/all`, {
        params: { page, limit: pagination.limit },
      })
      .then((response) => {
        setEvents(response.data.data || []);
        setPagination(response.data.pagination || {});
      })
      .catch((error) => {
        console.error("Error fetching events:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDelete = (event) => {
    Swal.fire({
      title: "Delete Event?",
      text: `Are you sure you want to delete "${event.name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c51369",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        setDeleteLoading(event._id);
        axios
          .delete(
            `${process.env.REACT_APP_API_URL}/event/delete/${event._id}`
          )
          .then(() => {
            fetchEvents(pagination.page);
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Event has been deleted.",
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text:
                error.response?.data?.message || "Failed to delete event.",
            });
          })
          .finally(() => {
            setDeleteLoading(null);
          });
      }
    });
  };

  const handleStatusChange = (event, newStatus) => {
    const statusLabels = {
      active: "Activate",
      completed: "Complete",
      cancelled: "Cancel",
    };

    Swal.fire({
      title: `${statusLabels[newStatus]} Event?`,
      text: `Change status of "${event.name}" to ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#c51369",
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Yes, ${statusLabels[newStatus]}`,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            `${process.env.REACT_APP_API_URL}/event/update/${event._id}`,
            { status: newStatus }
          )
          .then(() => {
            fetchEvents(pagination.page);
            Swal.fire({
              icon: "success",
              title: "Updated!",
              text: `Event status changed to ${newStatus}.`,
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text:
                error.response?.data?.message ||
                "Failed to update event status.",
            });
          });
      }
    });
  };

  const handleDistributeRewards = (event) => {
    Swal.fire({
      title: "Distribute Rewards?",
      text: `This will distribute rewards to top participants of "${event.name}". Continue?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#c51369",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Distribute",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post(
            `${process.env.REACT_APP_API_URL}/event/${event._id}/distribute-rewards`
          )
          .then((response) => {
            const rewards = response.data.data || [];
            const rewardsList = rewards
              .map(
                (r) =>
                  `Rank #${r.rank}: ${r.nickName} — ${r.rewardValue} ${r.rewardType}`
              )
              .join("<br>");
            Swal.fire({
              icon: "success",
              title: "Rewards Distributed!",
              html: rewardsList || "No rewards to distribute.",
            });
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text:
                error.response?.data?.message ||
                "Failed to distribute rewards.",
            });
          });
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // If showing leaderboard or dashboard sub-views
  if (showLeaderboard && selectedEvent) {
    return (
      <EventLeaderboardAdmin
        event={selectedEvent}
        onBack={() => {
          setShowLeaderboard(false);
          setSelectedEvent(null);
        }}
      />
    );
  }

  if (showDashboard && selectedEvent) {
    return (
      <EventDashboard
        event={selectedEvent}
        onBack={() => {
          setShowDashboard(false);
          setSelectedEvent(null);
        }}
      />
    );
  }

  return (
    <Fragment>
      <div className="mx-auto mt-5 w-auto rounded-t-2xl">
        <div className="rounded-2xl bg-white py-5 shadow-sm">
          <div className="flex flex-col items-center justify-between space-y-4 p-5 text-navy-600 shadow-sm md:flex-row md:space-y-0">
            <div>
              <h2 className="text-2xl font-semibold tracking-wide">
                🎉 Event Management
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Create and manage in-app events with point criteria and peak
                hours
              </p>
            </div>
            <div className="flex w-full justify-between md:w-auto">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 rounded-full bg-[#c51369] px-4 py-2 text-white hover:bg-[#a10f5b] hover:shadow-lg focus:outline-none"
              >
                <HiPlus className="h-5 w-5" />
                <span className="hidden md:inline">Create Event</span>
              </button>
            </div>
          </div>
        </div>

        {/* Events Table */}
        <div className="mb-5 overflow-x-auto">
          {loading ? (
            <TableLoader />
          ) : events.length === 0 ? (
            <div className="rounded-b-2xl bg-white py-20 text-center">
              <p className="text-lg text-gray-400">No events found</p>
              <p className="mt-2 text-sm text-gray-400">
                Click "Create Event" to get started
              </p>
            </div>
          ) : (
            <table className="w-full border text-left text-sm">
              <thead className="bg-[#e0ced7] text-gray-700">
                <tr>
                  <th className="border py-2 text-center">Sl No.</th>
                  <th className="border py-2 text-center">Event Name</th>
                  <th className="border py-2 text-center">Start Date</th>
                  <th className="border py-2 text-center">End Date</th>
                  <th className="border py-2 text-center">Status</th>
                  <th className="border py-2 text-center">Participants</th>
                  <th className="border py-2 text-center">Peak Hours</th>
                  <th className="border py-2 text-center">Point Criteria</th>
                  <th className="border py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => (
                  <tr key={event._id} className="border-b hover:bg-gray-50">
                    <td className="border py-2 text-center">
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </td>
                    <td className="border py-2 text-center font-medium">
                      {event.name}
                    </td>
                    <td className="border py-2 text-center">
                      {formatDate(event.startDate)}
                    </td>
                    <td className="border py-2 text-center">
                      {formatDate(event.endDate)}
                    </td>
                    <td className="border py-2 text-center">
                      <span
                        className={`inline-block rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                          statusColors[event.status] || "bg-gray-100"
                        }`}
                      >
                        {event.status}
                      </span>
                    </td>
                    <td className="border py-2 text-center font-semibold">
                      {event.totalParticipants || 0}
                    </td>
                    <td className="border py-2 text-center">
                      {event.peakHours?.length > 0
                        ? event.peakHours.map((ph, i) => (
                            <div key={i} className="text-xs">
                              {ph.startTime}-{ph.endTime} ({ph.multiplier}x)
                            </div>
                          ))
                        : "-"}
                    </td>
                    <td className="border py-2 text-center">
                      {event.pointCriteria?.length || 0} activities
                    </td>
                    <td className="border py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* Edit */}
                        <IconButton
                          size="small"
                          title="Edit Event"
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowEditModal(true);
                          }}
                        >
                          <FaEdit className="text-blue-600" />
                        </IconButton>

                        {/* Leaderboard */}
                        <IconButton
                          size="small"
                          title="View Leaderboard"
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowLeaderboard(true);
                          }}
                        >
                          <FaTrophy className="text-yellow-600" />
                        </IconButton>

                        {/* Dashboard */}
                        <IconButton
                          size="small"
                          title="View Dashboard"
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowDashboard(true);
                          }}
                        >
                          <FaChartBar className="text-green-600" />
                        </IconButton>

                        {/* Diagnose */}
                        <IconButton
                          size="small"
                          title="Diagnose Double Counts"
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowDiagnose(true);
                          }}
                        >
                          <FaBug className="text-red-500" />
                        </IconButton>

                        {/* Status toggles */}
                        {event.status === "draft" && (
                          <button
                            onClick={() =>
                              handleStatusChange(event, "active")
                            }
                            className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                            title="Activate Event"
                          >
                            Activate
                          </button>
                        )}
                        {event.status === "active" && (
                          <button
                            onClick={() =>
                              handleStatusChange(event, "completed")
                            }
                            className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                            title="Complete Event"
                          >
                            Complete
                          </button>
                        )}
                        {event.status === "completed" &&
                          event.rewards?.length > 0 && (
                            <button
                              onClick={() => handleDistributeRewards(event)}
                              className="rounded bg-yellow-500 px-2 py-1 text-xs text-white hover:bg-yellow-600"
                              title="Distribute Rewards"
                            >
                              Rewards
                            </button>
                          )}

                        {/* Delete */}
                        <IconButton
                          size="small"
                          title="Delete Event"
                          onClick={() => handleDelete(event)}
                          disabled={deleteLoading === event._id}
                        >
                          {deleteLoading === event._id ? (
                            <BtnLoader />
                          ) : (
                            <FaTrash className="text-red-500" />
                          )}
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pb-5">
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchEvents(pagination.page - 1)}
              className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchEvents(pagination.page + 1)}
              className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateEditEventModal
        open={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        fetchEvents={() => fetchEvents(pagination.page)}
        editData={null}
      />

      {/* Edit Modal */}
      {selectedEvent && (
        <CreateEditEventModal
          open={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
          }}
          fetchEvents={() => fetchEvents(pagination.page)}
          editData={selectedEvent}
        />
      )}
    {/* Diagnose Modal */}
      {selectedEvent && (
        <DiagnoseDoubleCountsModal
          open={showDiagnose}
          onClose={() => {
            setShowDiagnose(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
        />
      )}
    </Fragment>
  );
};

export default EventManagement;
